import React, { useState } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Clock, 
  CheckSquare, 
  Tag, 
  AlertCircle,
  FolderKanban,
  Kanban as KanbanIcon,
  List as ListIcon
} from 'lucide-react';
import TaskModal from './TaskModal';

export default function ProjectsBoard({ 
  projects, 
  currentProjectId, 
  setCurrentProjectId, 
  tasks, 
  team, 
  onAddTask, 
  onUpdateTask, 
  onDeleteTask,
  onOpenNewTask 
}) {
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'list'
  const [selectedTask, setSelectedTask] = useState(null);
  const [filterPriority, setFilterPriority] = useState('All');
  const [filterAssignee, setFilterAssignee] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  const currentProject = projects.find(p => p.id === currentProjectId) || projects[0];

  const projectTasks = tasks.filter(t => t.projectId === currentProject?.id).filter(t => {
    const matchesPriority = filterPriority === 'All' || t.priority === filterPriority;
    const matchesAssignee = filterAssignee === 'All' || t.assigneeId === filterAssignee;
    const matchesSearch = t.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          t.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesPriority && matchesAssignee && matchesSearch;
  });

  const columns = [
    { id: 'backlog', label: 'Backlog', color: 'border-slate-300 bg-slate-50/50' },
    { id: 'todo', label: 'To Do', color: 'border-blue-300 bg-blue-50/20' },
    { id: 'in_progress', label: 'In Progress', color: 'border-indigo-300 bg-indigo-50/20' },
    { id: 'review', label: 'Review', color: 'border-amber-300 bg-amber-50/20' },
    { id: 'done', label: 'Done', color: 'border-emerald-300 bg-emerald-50/20' },
  ];

  const moveTaskStatus = (taskId, newStatus) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      onUpdateTask({ ...task, status: newStatus });
    }
  };

  const priorityBadgeColors = {
    Urgent: 'bg-rose-100 text-rose-700',
    High: 'bg-amber-100 text-amber-700',
    Medium: 'bg-blue-100 text-blue-700',
    Low: 'bg-slate-100 text-slate-700'
  };

  return (
    <div className="p-8 space-y-6 animate-fadeIn flex flex-col h-[calc(100vh-4rem)] overflow-hidden">
      
      {/* Top Header / Project Selector */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 flex-shrink-0">
        <div className="flex items-center space-x-3 overflow-x-auto pb-2 md:pb-0">
          {projects.map(proj => (
            <button
              key={proj.id}
              onClick={() => setCurrentProjectId(proj.id)}
              className={`px-4 py-2.5 rounded-xl font-bold text-sm transition flex items-center gap-2 whitespace-nowrap ${
                proj.id === currentProject?.id
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              <FolderKanban className="w-4 h-4" />
              <span>{proj.name}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${proj.id === currentProject?.id ? 'bg-indigo-700 text-white' : 'bg-slate-200 text-slate-700'}`}>
                {tasks.filter(t => t.projectId === proj.id).length}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center space-x-3 flex-shrink-0">
          {/* View mode toggle */}
          <div className="bg-slate-200 p-1 rounded-xl flex items-center">
            <button
              onClick={() => setViewMode('kanban')}
              className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition ${viewMode === 'kanban' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
            >
              <KanbanIcon className="w-4 h-4" /> Kanban
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition ${viewMode === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
            >
              <ListIcon className="w-4 h-4" /> List
            </button>
          </div>

          <button
            onClick={onOpenNewTask}
            className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm rounded-xl transition shadow-md shadow-indigo-600/20 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Task
          </button>
        </div>
      </div>

      {/* Project Banner & Filters */}
      <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4 flex-shrink-0">
        <div>
          <div className="flex items-center space-x-2">
            <h2 className="text-base font-bold text-slate-900">{currentProject?.name}</h2>
            <span className="text-xs font-mono bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">{currentProject?.key}</span>
            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{currentProject?.status}</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">{currentProject?.description} • Budget: {currentProject?.budget}</p>
        </div>

        <div className="flex items-center space-x-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-60">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input 
              type="text"
              placeholder="Filter tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full text-xs bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <select 
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="All">All Priorities</option>
            <option value="Urgent">Urgent</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>

          <select 
            value={filterAssignee}
            onChange={(e) => setFilterAssignee(e.target.value)}
            className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="All">All Assignees</option>
            {team.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Kanban View */}
      {viewMode === 'kanban' ? (
        <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-4 overflow-x-auto pb-4">
          {columns.map(col => {
            const colTasks = projectTasks.filter(t => t.status === col.id);
            return (
              <div key={col.id} className={`flex flex-col rounded-2xl border ${col.color} p-4 h-full min-w-[260px] overflow-hidden`}>
                
                {/* Column Header */}
                <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-200/60 flex-shrink-0">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">{col.label}</h3>
                    <span className="text-xs font-mono font-semibold px-2 py-0.5 bg-white text-slate-600 rounded-full shadow-xs">
                      {colTasks.length}
                    </span>
                  </div>
                  <button 
                    onClick={onOpenNewTask}
                    className="p-1 hover:bg-slate-200/60 rounded-lg text-slate-500 transition"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {/* Column Cards */}
                <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                  {colTasks.map(task => {
                    const assignee = team.find(m => m.id === task.assigneeId);
                    const completedChecklist = task.checklist?.filter(c => c.completed).length || 0;
                    const totalChecklist = task.checklist?.length || 0;

                    return (
                      <div
                        key={task.id}
                        onClick={() => setSelectedTask(task)}
                        className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs hover:shadow-md transition cursor-pointer group space-y-3"
                      >
                        <div className="flex items-start justify-between">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${priorityBadgeColors[task.priority]}`}>
                            {task.priority}
                          </span>
                          <span className="text-[10px] text-slate-400 font-mono">{task.id}</span>
                        </div>

                        <h4 className="text-xs font-bold text-slate-900 group-hover:text-indigo-600 transition leading-snug">
                          {task.title}
                        </h4>

                        {task.tags && task.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {task.tags.map(t => (
                              <span key={t} className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-medium">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}

                        {totalChecklist > 0 && (
                          <div className="flex items-center space-x-1.5 text-xs text-slate-500">
                            <CheckSquare className="w-3.5 h-3.5 text-slate-400" />
                            <span>{completedChecklist}/{totalChecklist}</span>
                          </div>
                        )}

                        <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs">
                          <div className="flex items-center space-x-1.5">
                            {assignee && (
                              <img src={assignee.avatar} alt={assignee.name} className="w-5 h-5 rounded-full object-cover" />
                            )}
                            <span className="text-[11px] text-slate-600 truncate max-w-[90px]">{assignee?.name.split(' ')[0]}</span>
                          </div>
                          
                          {/* Quick Status Move Dropdown */}
                          <select 
                            value={task.status}
                            onChange={(e) => { e.stopPropagation(); moveTaskStatus(task.id, e.target.value); }}
                            onClick={(e) => e.stopPropagation()}
                            className="text-[10px] bg-slate-50 border border-slate-200 rounded px-1.5 py-1 text-slate-600 focus:outline-none"
                          >
                            <option value="backlog">Backlog</option>
                            <option value="todo">To Do</option>
                            <option value="in_progress">In Progress</option>
                            <option value="review">Review</option>
                            <option value="done">Done</option>
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>

              </div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="flex-1 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
          <div className="overflow-x-auto flex-1">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">
                  <th className="p-4">Task</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Priority</th>
                  <th className="p-4">Assignee</th>
                  <th className="p-4">Due Date</th>
                  <th className="p-4">Effort</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-medium">
                {projectTasks.map(task => {
                  const assignee = team.find(m => m.id === task.assigneeId);
                  return (
                    <tr 
                      key={task.id}
                      onClick={() => setSelectedTask(task)}
                      className="hover:bg-slate-50/80 cursor-pointer transition"
                    >
                      <td className="p-4 font-bold text-slate-900">
                        {task.title}
                        <p className="text-[11px] font-normal text-slate-500 line-clamp-1">{task.description}</p>
                      </td>
                      <td className="p-4">
                        <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700">
                          {task.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${priorityBadgeColors[task.priority]}`}>
                          {task.priority}
                        </span>
                      </td>
                      <td className="p-4 flex items-center space-x-2">
                        {assignee && <img src={assignee.avatar} alt={assignee.name} className="w-6 h-6 rounded-full object-cover" />}
                        <span>{assignee?.name}</span>
                      </td>
                      <td className="p-4 font-mono text-slate-500">{task.dueDate}</td>
                      <td className="p-4 font-mono text-slate-700">{task.estimatedHours} hrs</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskModal 
          task={selectedTask}
          team={team}
          onClose={() => setSelectedTask(null)}
          onUpdateTask={onUpdateTask}
          onDeleteTask={onDeleteTask}
        />
      )}

    </div>
  );
}
